import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { productionApi } from '../../api/production.api';
import { ParcelMap } from '../../components/production/ParcelMap';
import type { Zone, Plot } from '../../types/production.types';

const SOIL_TYPE_LABELS: Record<string, string> = {
  CLAY: 'Argileux',
  LOAM: 'Limoneux',
  SANDY: 'Sablonneux',
  CLAY_LOAM: 'Argilo-limoneux',
  PEAT: 'Tourbeux',
};

const PLOT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'En culture',
  FALLOW: 'En jachere',
  COVER_CROP: 'Engrais vert',
  PREPARATION: 'En preparation',
};

export default function ZonesAndPlots() {
  const { t } = useTranslation();
  const { tenant, loading: tenantLoading } = useTenant();

  const [zones, setZones] = useState<Zone[]>([]);
  const [cropPoints, setCropPoints] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedZone, setExpandedZone] = useState<string | null>(null);

  // Zone modal
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [zoneForm, setZoneForm] = useState({ code: '', name: '', area_m2: '', soil_type: '', irrigation_type: '', is_under_cover: false, cover_type: '', notes: '' });

  // Plot modal
  const [plotModalOpen, setPlotModalOpen] = useState(false);
  const [editingPlot, setEditingPlot] = useState<Plot | null>(null);
  const [plotZoneId, setPlotZoneId] = useState('');
  const [plotForm, setPlotForm] = useState({ code: '', name: '', length_m: '', width_m: '', area_m2: '', status: 'ACTIVE', orientation: '', notes: '' });

  useEffect(() => {
    if (!tenantLoading && tenant) loadZones();
  }, [tenant, tenantLoading]);

  const loadZones = async () => {
    try {
      setLoading(true);
      const [data, points] = await Promise.all([
        productionApi.listZones(),
        productionApi.listCropGpsPoints().catch(() => []),
      ]);
      setZones(data);
      setCropPoints(points);
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Zone CRUD
  const openZoneModal = (zone?: Zone) => {
    if (zone) {
      setEditingZone(zone);
      setZoneForm({
        code: zone.code, name: zone.name, area_m2: String(zone.area_m2),
        soil_type: zone.soil_type || '', irrigation_type: zone.irrigation_type || '',
        is_under_cover: zone.is_under_cover, cover_type: zone.cover_type || '', notes: zone.notes || '',
      });
    } else {
      setEditingZone(null);
      setZoneForm({ code: '', name: '', area_m2: '', soil_type: '', irrigation_type: '', is_under_cover: false, cover_type: '', notes: '' });
    }
    setZoneModalOpen(true);
  };

  const saveZone = async () => {
    try {
      const data: any = {
        code: zoneForm.code, name: zoneForm.name, area_m2: parseFloat(zoneForm.area_m2),
        soil_type: zoneForm.soil_type || null, irrigation_type: zoneForm.irrigation_type || null,
        is_under_cover: zoneForm.is_under_cover, cover_type: zoneForm.cover_type || null, notes: zoneForm.notes || null,
      };
      if (editingZone) {
        await productionApi.updateZone(editingZone.id, data);
      } else {
        await productionApi.createZone(data);
      }
      setZoneModalOpen(false);
      loadZones();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  const deleteZone = async (id: string) => {
    if (!confirm('Supprimer cette zone ?')) return;
    try {
      await productionApi.deleteZone(id);
      loadZones();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  // Plot CRUD
  const openPlotModal = (zoneId: string, plot?: Plot) => {
    setPlotZoneId(zoneId);
    if (plot) {
      setEditingPlot(plot);
      setPlotForm({
        code: plot.code, name: plot.name || '', length_m: plot.length_m ? String(plot.length_m) : '',
        width_m: plot.width_m ? String(plot.width_m) : '', area_m2: String(plot.area_m2),
        status: plot.status, orientation: plot.orientation || '', notes: plot.notes || '',
      });
    } else {
      setEditingPlot(null);
      setPlotForm({ code: '', name: '', length_m: '', width_m: '', area_m2: '', status: 'ACTIVE', orientation: '', notes: '' });
    }
    setPlotModalOpen(true);
  };

  const savePlot = async () => {
    try {
      const data: any = {
        zone_id: plotZoneId, code: plotForm.code, name: plotForm.name || null,
        length_m: plotForm.length_m ? parseFloat(plotForm.length_m) : null,
        width_m: plotForm.width_m ? parseFloat(plotForm.width_m) : null,
        area_m2: parseFloat(plotForm.area_m2), status: plotForm.status,
        orientation: plotForm.orientation || null, notes: plotForm.notes || null,
      };
      if (editingPlot) {
        await productionApi.updatePlot(editingPlot.id, data);
      } else {
        await productionApi.createPlot(data);
      }
      setPlotModalOpen(false);
      loadZones();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('production.zones.title', 'Parcellaire')}
        </h1>
        <button
          onClick={() => openZoneModal()}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm"
        >
          + {t('production.zones.createZone', 'Nouvelle zone')}
        </button>
      </div>

      {/* Carte satellite du parcellaire (zones + variétés géolocalisées) */}
      <ParcelMap zones={zones} cropPoints={cropPoints} />

      {zones.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
          Aucune zone creee. Commencez par ajouter vos zones de culture.
        </div>
      ) : (
        <div className="space-y-3">
          {zones.map((zone) => (
            <div key={zone.id} className="bg-white rounded-lg shadow">
              {/* Zone header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedZone(expandedZone === zone.id ? null : zone.id)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{zone.is_under_cover ? '🏠' : '🌾'}</span>
                  <div>
                    <h3 className="font-semibold">
                      {zone.code} — {zone.name}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {zone.area_m2} m²
                      {zone.soil_type && ` · ${SOIL_TYPE_LABELS[zone.soil_type] || zone.soil_type}`}
                      {zone.irrigation_type && ` · ${zone.irrigation_type}`}
                      {zone.is_under_cover && zone.cover_type && ` · ${zone.cover_type}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {zone.plots?.length || 0} planches
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); openZoneModal(zone); }}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteZone(zone.id); }}
                    className="text-gray-400 hover:text-red-600 text-sm"
                  >
                    🗑️
                  </button>
                  <span className="text-gray-400">{expandedZone === zone.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Plots list */}
              {expandedZone === zone.id && (
                <div className="border-t px-4 pb-4">
                  <div className="flex items-center justify-between py-2">
                    <h4 className="text-sm font-medium text-gray-600">Planches</h4>
                    <button
                      onClick={() => openPlotModal(zone.id)}
                      className="text-green-600 text-sm hover:text-green-700"
                    >
                      + Ajouter
                    </button>
                  </div>
                  {(!zone.plots || zone.plots.length === 0) ? (
                    <p className="text-sm text-gray-400">Aucune planche dans cette zone</p>
                  ) : (
                    <div className="grid gap-2">
                      {zone.plots.map((plot) => (
                        <div key={plot.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <span className="font-medium text-sm">{plot.code}</span>
                            {plot.name && <span className="text-sm text-gray-500 ml-2">{plot.name}</span>}
                            <span className="text-xs text-gray-400 ml-2">{plot.area_m2} m²</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              plot.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                              plot.status === 'FALLOW' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {PLOT_STATUS_LABELS[plot.status] || plot.status}
                            </span>
                            <button
                              onClick={() => openPlotModal(zone.id, plot)}
                              className="text-gray-400 hover:text-gray-600 text-xs"
                            >
                              ✏️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Zone Modal */}
      {zoneModalOpen && (
        <Modal title={editingZone ? 'Modifier la zone' : 'Nouvelle zone'} onClose={() => setZoneModalOpen(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input className="input" placeholder="Code (ex: A, TUNNEL-1)" value={zoneForm.code} onChange={(e) => setZoneForm({ ...zoneForm, code: e.target.value })} />
              <input className="input" placeholder="Nom" value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} />
            </div>
            <input className="input" type="number" placeholder="Surface (m²)" value={zoneForm.area_m2} onChange={(e) => setZoneForm({ ...zoneForm, area_m2: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <select className="input" value={zoneForm.soil_type} onChange={(e) => setZoneForm({ ...zoneForm, soil_type: e.target.value })}>
                <option value="">Type de sol</option>
                {Object.entries(SOIL_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <input className="input" placeholder="Irrigation" value={zoneForm.irrigation_type} onChange={(e) => setZoneForm({ ...zoneForm, irrigation_type: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={zoneForm.is_under_cover} onChange={(e) => setZoneForm({ ...zoneForm, is_under_cover: e.target.checked })} />
              Sous abri (tunnel/serre)
            </label>
            {zoneForm.is_under_cover && (
              <input className="input" placeholder="Type d'abri (ex: tunnel-plastique)" value={zoneForm.cover_type} onChange={(e) => setZoneForm({ ...zoneForm, cover_type: e.target.value })} />
            )}
            <textarea className="input" rows={2} placeholder="Notes" value={zoneForm.notes} onChange={(e) => setZoneForm({ ...zoneForm, notes: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setZoneModalOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Annuler</button>
              <button onClick={saveZone} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Enregistrer</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Plot Modal */}
      {plotModalOpen && (
        <Modal title={editingPlot ? 'Modifier la planche' : 'Nouvelle planche'} onClose={() => setPlotModalOpen(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input className="input" placeholder="Code (ex: A1)" value={plotForm.code} onChange={(e) => setPlotForm({ ...plotForm, code: e.target.value })} />
              <input className="input" placeholder="Nom (optionnel)" value={plotForm.name} onChange={(e) => setPlotForm({ ...plotForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input className="input" type="number" placeholder="Longueur (m)" value={plotForm.length_m} onChange={(e) => setPlotForm({ ...plotForm, length_m: e.target.value })} />
              <input className="input" type="number" placeholder="Largeur (m)" value={plotForm.width_m} onChange={(e) => setPlotForm({ ...plotForm, width_m: e.target.value })} />
              <input className="input" type="number" placeholder="Surface (m²)" value={plotForm.area_m2} onChange={(e) => setPlotForm({ ...plotForm, area_m2: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select className="input" value={plotForm.status} onChange={(e) => setPlotForm({ ...plotForm, status: e.target.value })}>
                {Object.entries(PLOT_STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <input className="input" placeholder="Orientation (ex: N-S)" value={plotForm.orientation} onChange={(e) => setPlotForm({ ...plotForm, orientation: e.target.value })} />
            </div>
            <textarea className="input" rows={2} placeholder="Notes" value={plotForm.notes} onChange={(e) => setPlotForm({ ...plotForm, notes: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setPlotModalOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Annuler</button>
              <button onClick={savePlot} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Enregistrer</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}
